import { isValidElement, ReactElement, ReactNode, useState } from 'react';
import { formatDateTime } from '@bits/i18n';
import { Icon } from '@phosphor-icons/react';

import { ExpandableCaret } from '../expandable-caret';
import { JSONTable } from '../json-table';
import { Pagination } from '../pagination-and-filtering/pagination';
import { Skeleton } from '../skeleton';
import { cn } from '../utils/cn';

export type EventLogEvent<TEventType extends string> = {
  createdAt: Date;
  id: string;
  icon: ReactElement | Icon;
  actor: {
    name: ReactNode | string;
  };
  label: ReactNode;
  type: TEventType;
  rawPayload?: Record<string, unknown>;
};

export type EventLogProps<TEventType extends string> = {
  accessibleName: string;
  events: EventLogEvent<TEventType>[];
  loading: boolean;
  navigating: boolean;
  pagination: {
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
  };
};

export const EventLog = <TEventType extends string>({
  accessibleName,
  events,
  loading,
  navigating,
  pagination,
}: EventLogProps<TEventType>) => {
  return (
    <div className="-mx-3 space-y-4">
      {loading ||
        (navigating && (
          <p className="sr-only" aria-live="assertive">
            Loading...
          </p>
        ))}

      <table
        aria-label={accessibleName}
        className={cn('w-full', navigating && 'opacity-50')}
        aria-live="polite"
      >
        <thead className="sr-only">
          <tr>
            <th>Date</th>
            <th aria-hidden>Icon</th>
            <th>Event</th>
          </tr>
        </thead>

        {!loading &&
          events.length > 0 &&
          events.map((event) => <EventLogRow key={event.id} event={event} />)}

        {!loading && events.length === 0 && (
          <tr>
            <td colSpan={3} className="px-3 py-4 text-center">
              No events found.
            </td>
          </tr>
        )}

        {loading &&
          Array.from({ length: 10 }).map((_, index) => (
            <tr key={index}>
              <td className="px-3 py-1">
                <Skeleton className="h-8 w-32" />
              </td>
              <td className="py-1">
                <Skeleton className="size-8" />
              </td>
              <td className="w-full px-3 py-1">
                <Skeleton className="h-8 w-full" />
              </td>
            </tr>
          ))}
      </table>

      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        onPageChange={pagination.onPageChange}
      />
    </div>
  );
};

const EventLogRow = ({ event }: { event: EventLogEvent<string> }) => {
  const [expanded, setExpanded] = useState(false);

  const Wrapper = event.rawPayload ? 'button' : 'div';

  const handleClick = () => {
    if (event.rawPayload) {
      setExpanded(!expanded);
    }
  };

  return (
    <tr
      key={event.id}
      className="block gap-2 py-2 not-last:border-b lg:table-row lg:not-last:border-b-0"
    >
      <td className="inline-block px-3 py-1 align-top lg:table-cell lg:w-0">
        <span className="bits-text-body-2 relative top-1.5 flex items-center gap-1 whitespace-nowrap text-ink/45">
          {formatDateTime(event.createdAt)}
        </span>
      </td>
      <td
        className="inline-block w-0 pr-2 pt-[13px] align-top lg:table-cell"
        aria-hidden
      >
        <EventLogIcon icon={event.icon} />
      </td>
      <td className="block lg:table-cell">
        <Wrapper
          className={cn(
            'bits-text-body-2 flex w-full items-center gap-2 px-3 py-1 text-left lg:bits-text-body-1',
            event.rawPayload && 'cursor-pointer rounded-md hover:bg-ink/5'
          )}
          onClick={handleClick}
          aria-expanded={expanded}
        >
          {event.rawPayload && <ExpandableCaret expanded={expanded} />}
          <EventLogRowLabel event={event} />
        </Wrapper>

        {event.rawPayload && expanded && (
          <div className="ml-10 mt-2">
            <JSONTable json={event.rawPayload} />
          </div>
        )}
      </td>
    </tr>
  );
};

const EventLogRowLabel = ({ event }: { event: EventLogEvent<string> }) => {
  return (
    <div className="align-top">
      {event.actor?.name && (
        <>
          <div className="inline font-bold">{event.actor.name}</div>{' '}
        </>
      )}
      <div className="inline">{event.label}</div>
    </div>
  );
};

const EventLogIcon = ({ icon }: { icon: ReactElement | Icon }) => {
  if (isValidElement(icon)) {
    return icon;
  }

  const Icon = icon as Icon;

  return <Icon className="size-4" />;
};
